import React, { useState } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert } from '@mui/material';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { Project } from '../types';
import { createDefaultDeploymentStatus, validateProject, DeploymentStage, NodeRole } from '../types';
import { Loading } from '../components/Loading';
import { ProjectView } from '../components/ProjectView';
import { ProjectEdit } from '../components/ProjectEdit';
import { ProjectProgress } from '../components/ProjectProgress';
import { Layout } from '../components/Layout';

export const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [project, setProject] = useState<Project | null>(null);
  const [controlNodes, setControlNodes] = useState<Record<string, number>>({});
  const [workerNodes, setWorkerNodes] = useState<Record<string, number>>({});
  const [nodesToDelete, setNodesToDelete] = useState<string[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load project data
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });

  // Load node types and applications
  const { data: nodeTypes, isLoading: nodeTypesLoading } = useQuery({
    queryKey: ['nodeTypes'],
    queryFn: () => api.getNodeTypes(),
  });

  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.getApplications(),
  });

  // Poll for status updates
  const { data: statusData } = useQuery({
    queryKey: ['status', id],
    queryFn: () => api.getStatus(id!),
    enabled: !!id,
    refetchInterval: 1000,
  });

  const status = statusData?.status || createDefaultDeploymentStatus();
  const nodes = statusData?.nodes || [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: { id: string; project: Project; controlNodes: Record<string, number>; workerNodes: Record<string, number>; nodesToDelete: string[] }) =>
      api.updateProject(data.id, data.project, data.controlNodes, data.workerNodes, data.nodesToDelete),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      setEditMode(false);
      setNodesToDelete([]);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save project. Please try again.');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => api.deleteProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/');
    },
    onError: (err) => {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project. Please try again.');
    },
  });

  // Sync local state with query data
  React.useEffect(() => {
    if (projectData && !project) {
      setProject(projectData);
    }
  }, [projectData, project]);

  // Populate node counts from actual provisioned nodes
  React.useEffect(() => {
    if (nodes.length > 0 && Object.keys(controlNodes).length === 0 && Object.keys(workerNodes).length === 0) {
      const activeNodes = nodes.filter(n => !n.deletedAt);

      // Count control nodes by type
      const controlCounts: Record<string, number> = {};
      activeNodes
        .filter(n => n.role === NodeRole.CONTROL_NODE)
        .forEach(n => {
          controlCounts[n.type] = (controlCounts[n.type] || 0) + 1;
        });

      // Count worker nodes by type
      const workerCounts: Record<string, number> = {};
      activeNodes
        .filter(n => n.role === NodeRole.WORKER_NODE)
        .forEach(n => {
          workerCounts[n.type] = (workerCounts[n.type] || 0) + 1;
        });

      if (Object.keys(controlCounts).length > 0) {
        setControlNodes(controlCounts);
      }
      if (Object.keys(workerCounts).length > 0) {
        setWorkerNodes(workerCounts);
      }
    }
  }, [nodes, controlNodes, workerNodes]);

  const handleSave = () => {
    if (!project || !id) return;
    updateMutation.mutate({
      id,
      project,
      controlNodes,
      workerNodes,
      nodesToDelete
    });
  };

  const handleRevert = () => {
    setProject(projectData || null);
    setControlNodes({});
    setWorkerNodes({});
    setNodesToDelete([]);
    setEditMode(false);
    setError(null);
  };

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
  };

  const handleDelete = () => {
    if (!id) return;
    setDeleteError(null);
    deleteMutation.mutate(id);
  };

  if (projectLoading || nodeTypesLoading || applicationsLoading || !project || !nodeTypes || !applications || !statusData) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  const showProgress = [
    DeploymentStage.CONFIGURATION,
    DeploymentStage.PROVISIONING,
    DeploymentStage.SETUP_CLUSTER,
    DeploymentStage.SETUP_APPLICATIONS,
  ].includes(status.stage);

  if (showProgress) {
    return (
      <Layout>
        <ProjectProgress status={status} nodes={nodes} />
      </Layout>
    );
  }

  const validation = validateProject(project, controlNodes, workerNodes);
  const canSave = !validation.some((v) => v.preventContinue);

  return (
    <Layout>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {editMode ? (
        <>
          <ProjectEdit
            project={project}
            controlNodes={controlNodes}
            workerNodes={workerNodes}
            nodes={nodes}
            nodesToDelete={nodesToDelete}
            onChange={setProject}
            onControlNodesChange={setControlNodes}
            onWorkerNodesChange={setWorkerNodes}
            onNodesToDeleteChange={setNodesToDelete}
            nodeTypes={nodeTypes}
            applications={applications}
            validation={validation}
          />
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={!canSave || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Apply'}
            </Button>
            <Button variant="outlined" size="large" onClick={handleRevert}>
              Revert
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Box>
        </>
      ) : (
        <>
          <ProjectView
            project={project}
            controlNodes={controlNodes}
            workerNodes={workerNodes}
            status={status}
            nodes={nodes}
            nodeTypes={nodeTypes}
            applications={applications}
          />
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" size="large" onClick={handleEdit}>
              Edit
            </Button>
          </Box>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setDeleteError(null); }}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete this project? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteError(null); }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};
